import AffiliateInfo from '@/components/affiliate/AffiliateInfo';
import { CommissionRewardReviewSummaryNotice } from '@/components/orders/commission-reward-review-notice';
import TableEmptyState from '@/components/table/TableEmptyState';
import TableSkeleton from '@/components/table/TableSkeleton';
import {
  TableScrollArea,
  stickyColumnProps,
  stickyEndColumnProps,
} from '@/components/table/TableScrollArea';
import Money from '@/components/text/money';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPanel } from '@/components/ui/filter-panel';
import { useCreatePayoutAction } from '@/hooks/actions/use-create-payout-action';
import { usePendingCommissionsQuery } from '@/hooks/queries/use-pending-commissions-query';
import { usePendingPayoutStore } from '@/stores/pending-payout-store';
import type { PayableAffiliateCommission } from '@/types/commission';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Skeleton,
  Span,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { PendingCommissionsFilters } from './PendingCommissions.Filters';
import { PendingCommissionsActionBar } from './PendingCommissionsActionBar';
import { MoneySquare, WarningTriangle } from 'iconoir-react';

interface PendingCommissionsTableProps {}

const SELECTION_COLUMN_WIDTH = 'calc(var(--table-bleed-x) + 2rem)';
const AFFILIATE_COLUMN_WIDTH = '260px';
const STATUS_COLUMN_WIDTH = '180px';
const ACTIONS_COLUMN_WIDTH = '104px';
const PENDING_COMMISSIONS_TABLE_STYLE = {
  '--table-selection-padding-start': 'var(--table-bleed-x)',
} as CSSProperties;

export const PendingCommissionsTable: React.FC<PendingCommissionsTableProps> = () => {
  const { t } = useTranslation();
  // Select state individually using separate hook calls
  const affiliateIds = usePendingPayoutStore((state) => state.filters.affiliateIds);
  const companionPreselection = usePendingPayoutStore((state) => state.companionPreselection);
  const clearCompanionPreselection = usePendingPayoutStore(
    (state) => state.clearCompanionPreselection
  );
  const selectedCommissionKeys = usePendingPayoutStore((state) => state.selectedCommissionKeys);
  const toggleCommissionSelection = usePendingPayoutStore(
    (state) => state.toggleCommissionSelection
  );
  const selectAllCommissions = usePendingPayoutStore((state) => state.selectAllCommissions);
  const clearSelection = usePendingPayoutStore((state) => state.clearSelection);
  const createPayoutMutation = useCreatePayoutAction();

  // Clear selection when the component unmounts
  useEffect(() => {
    return () => {
      clearSelection();
      clearCompanionPreselection();
    };
  }, [clearSelection, clearCompanionPreselection]);

  // Memoize the filters object for the query based on the primitive values
  const queryFilters = useMemo(() => ({ affiliateIds }), [affiliateIds]);

  // Pass the memoized filters object to the query hook
  const { data: commissionData, isLoading, isError } = usePendingCommissionsQuery(queryFilters);

  const commissions = useMemo(
    () => commissionData?.commissions ?? [],
    [commissionData?.commissions]
  );

  const payableCommissions = useMemo(
    () => commissions.filter((item) => item.paymentEligibility.status === 'ready'),
    [commissions]
  );
  const hasRewardReviews = commissionData?.rewardReviews?.some(
    ({ commissionCount }) => commissionCount > 0
  );

  // =====================
  // Utility Functions (Component Scope)
  // =====================
  const getUserName = useCallback(
    (item: PayableAffiliateCommission) =>
      [item.user.firstName, item.user.lastName].filter(Boolean).join(' ') || 'Unknown',
    []
  );

  // Helper function to create a unique key for each commission row
  const getCommissionKey = useCallback((affiliateId: string) => `${affiliateId}`, []);

  const handleSelectAllChange = (changes: { checked: boolean | 'indeterminate' }) => {
    if (changes.checked === true) {
      selectAllCommissions(payableCommissions.map((item) => getCommissionKey(item.affiliateId)));
    } else {
      clearSelection();
    }
  };

  const handleRowCheckboxChange = (
    _changes: { checked: boolean | 'indeterminate' },
    affiliateId: string
  ) => {
    const commission = commissions.find((item) => item.affiliateId === affiliateId);
    if (commission?.paymentEligibility.status !== 'ready') {
      return;
    }

    toggleCommissionSelection(getCommissionKey(affiliateId));
  };

  const selectedPayableCommissionKeys = selectedCommissionKeys.filter((key) =>
    payableCommissions.some((item) => getCommissionKey(item.affiliateId) === key)
  );

  const areAllSelected =
    payableCommissions.length > 0 &&
    selectedPayableCommissionKeys.length === payableCommissions.length;
  const isIndeterminate = selectedPayableCommissionKeys.length > 0 && !areAllSelected;

  const hasSelection = selectedPayableCommissionKeys.length > 0;

  const handlePaySelected = useCallback(() => {
    const selectedCommissionsPayload = commissions
      .filter(
        (item) =>
          item.paymentEligibility.status === 'ready' &&
          selectedPayableCommissionKeys.includes(getCommissionKey(item.affiliateId))
      )
      .map((item) => ({
        affiliateId: item.affiliateId,
      }));

    if (selectedCommissionsPayload.length === 0) {
      return;
    }

    createPayoutMutation.mutate({
      commissions: selectedCommissionsPayload,
    });
  }, [commissions, selectedPayableCommissionKeys, createPayoutMutation, getCommissionKey]);

  const handlePaySingle = useCallback(
    (item: PayableAffiliateCommission) => {
      if (item.paymentEligibility.status !== 'ready') {
        return;
      }

      createPayoutMutation.mutate({
        commissions: [{ affiliateId: item.affiliateId }],
      });
    },
    [createPayoutMutation]
  );

  const handlePayAll = useCallback(() => {
    const allCommissionsPayload = payableCommissions.map((item) => ({
      affiliateId: item.affiliateId,
    }));

    if (allCommissionsPayload.length === 0) {
      return;
    }
    createPayoutMutation.mutate({ commissions: allCommissionsPayload });
  }, [createPayoutMutation, payableCommissions]);

  if (isError) {
    return (
      <EmptyState
        title={t('commissions.pending.errorLoading')}
        icon={<WarningTriangle width={22} height={22} />}
      />
    );
  }

  const totalAmountAll = payableCommissions.reduce(
    (sum, item) => sum + (item?.amount?.amount ?? 0),
    0
  );
  const currencyCodeAll =
    payableCommissions.length > 0 ? payableCommissions[0].amount.currencyCode : 'EUR';

  const selectedCommissionsTotal = commissions
    .filter(
      (item) =>
        item.paymentEligibility.status === 'ready' &&
        selectedPayableCommissionKeys.includes(getCommissionKey(item.affiliateId))
    )
    .reduce((sum, item) => sum + (item?.amount?.amount ?? 0), 0);

  // TODO: This is a hack to get the currency code for the selected commissions
  // We should use the currency code from the program instead of the first commission
  const currencyCodeSelected =
    selectedPayableCommissionKeys.length > 0
      ? (commissions.find((item) =>
          selectedPayableCommissionKeys.includes(getCommissionKey(item.affiliateId))
        )?.amount.currencyCode ?? 'EUR')
      : currencyCodeAll;

  return (
    <VStack gap={5} w="full" align="stretch">
      <CommissionRewardReviewSummaryNotice summaries={commissionData?.rewardReviews} />
      {companionPreselection ? (
        // The card named beneficiaries, not amounts. What is owed is read again
        // here, so the brand pays what is true now rather than what was true
        // the night the card went up.
        <Box
          borderWidth="1px"
          borderColor="border"
          borderRadius="md"
          bg="bg.subtle"
          px={4}
          py={3}
          data-testid="companion-payout-preselection-notice"
        >
          <Text fontSize="14px" color="fg.muted">
            {t('commissions.pending.companionPreselection', {
              count: companionPreselection.affiliateIds.length,
            })}
          </Text>
        </Box>
      ) : null}
      <FilterPanel
        w="full"
        bodyProps={{ p: 3 }}
        actionsSlot={
          isLoading ? (
            <Skeleton height="40px" width="260px" />
          ) : (
            <Button
              size="sm"
              gap={1}
              alignSelf="flex-end"
              disabled={payableCommissions.length === 0 || createPayoutMutation.isPending}
              loading={
                createPayoutMutation.isPending &&
                createPayoutMutation.variables?.commissions.length === payableCommissions.length
              }
              onClick={handlePayAll}
            >
              {t('commissions.pending.payAll')} -
              <Span ml={1}>
                <Money fontSize="15px" value={totalAmountAll} currency={currencyCodeAll} />
              </Span>
            </Button>
          )
        }
      >
        <PendingCommissionsFilters />
      </FilterPanel>
      {isLoading ? (
        <TableSkeleton
          rows={5}
          columns={[
            { width: SELECTION_COLUMN_WIDTH },
            { width: AFFILIATE_COLUMN_WIDTH },
            { width: '100px' },
            { width: '140px' },
            { width: '140px' },
            { width: STATUS_COLUMN_WIDTH },
            { width: ACTIONS_COLUMN_WIDTH },
          ]}
          stickyColumns={2}
          stickyLastColumn
          firstColumnWidth={SELECTION_COLUMN_WIDTH}
          secondColumnWidth={AFFILIATE_COLUMN_WIDTH}
          lastColumnWidth={ACTIONS_COLUMN_WIDTH}
          scrollAreaStyle={PENDING_COMMISSIONS_TABLE_STYLE}
        />
      ) : (
        <TableScrollArea style={PENDING_COMMISSIONS_TABLE_STYLE}>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader
                  width={SELECTION_COLUMN_WIDTH}
                  {...stickyColumnProps(SELECTION_COLUMN_WIDTH, '0px', { divider: 'end' })}
                >
                  <Checkbox.Root
                    onCheckedChange={handleSelectAllChange}
                    checked={isIndeterminate ? 'indeterminate' : areAllSelected}
                    aria-label="Select all rows"
                    disabled={payableCommissions.length === 0}
                    size="sm"
                    colorPalette="primary"
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                  </Checkbox.Root>
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  width={AFFILIATE_COLUMN_WIDTH}
                  {...stickyColumnProps(AFFILIATE_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH)}
                >
                  {t('commissions.pending.table.affiliate')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="100px">
                  {t('commissions.pending.table.orders')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="140px">
                  {t('commissions.pending.table.sales')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="140px">
                  {t('commissions.pending.table.commissions')}
                </Table.ColumnHeader>
                <Table.ColumnHeader width={STATUS_COLUMN_WIDTH}>
                  {t('commissions.pending.table.status')}
                </Table.ColumnHeader>
                <Table.ColumnHeader
                  w={ACTIONS_COLUMN_WIDTH}
                  {...stickyEndColumnProps(ACTIONS_COLUMN_WIDTH)}
                />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {commissions.length === 0 ? (
                <TableEmptyState
                  colSpan={7}
                  icon={<MoneySquare width={22} height={22} />}
                  title={
                    hasRewardReviews
                      ? t('commissions.rewardReview.noPayable')
                      : affiliateIds && affiliateIds.length > 0
                        ? t('commissions.pending.noMatching')
                        : t('commissions.pending.noPending')
                  }
                />
              ) : (
                commissions.map((item) => {
                  const commissionKey = getCommissionKey(item.affiliateId);
                  const isPaymentReady = item.paymentEligibility.status === 'ready';
                  const eligibilityLabel = isPaymentReady
                    ? null
                    : t(`commissions.pending.paymentEligibility.${item.paymentEligibility.status}`);
                  return (
                    <Table.Row key={commissionKey} _hover={{ bg: 'gray.50' }}>
                      <Table.Cell
                        width={SELECTION_COLUMN_WIDTH}
                        {...stickyColumnProps(SELECTION_COLUMN_WIDTH, '0px', { divider: 'end' })}
                      >
                        <Checkbox.Root
                          colorPalette="primary"
                          onCheckedChange={(changes) =>
                            handleRowCheckboxChange(changes, item.affiliateId)
                          }
                          checked={
                            isPaymentReady && selectedPayableCommissionKeys.includes(commissionKey)
                          }
                          aria-label={`Select row for affiliate ${item.affiliateId}`}
                          disabled={!isPaymentReady}
                          size="sm"
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                        </Checkbox.Root>
                      </Table.Cell>
                      <Table.Cell
                        {...stickyColumnProps(AFFILIATE_COLUMN_WIDTH, SELECTION_COLUMN_WIDTH)}
                      >
                        <AffiliateInfo
                          name={getUserName(item)}
                          avatarUrl={item.user.avatar?.fileUrl || null}
                          programName={item.program.name}
                          size="xs"
                        />
                      </Table.Cell>
                      <Table.Cell>{item.orderCount}</Table.Cell>
                      <Table.Cell>
                        <Money
                          color="green.600"
                          prefix="+"
                          value={item.totalGeneratedSales.amount}
                          currency={item.totalGeneratedSales.currencyCode}
                          fontSize="14px"
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <Money
                          value={item.amount.amount}
                          currency={item.amount.currencyCode}
                          fontSize="14px"
                        />
                      </Table.Cell>
                      <Table.Cell width={STATUS_COLUMN_WIDTH}>
                        {eligibilityLabel ? (
                          <Badge
                            size="sm"
                            variant="subtle"
                            colorPalette={
                              item.paymentEligibility.status === 'restricted' ? 'red' : 'orange'
                            }
                          >
                            {eligibilityLabel}
                          </Badge>
                        ) : (
                          <Badge size="sm" variant="subtle" colorPalette="green">
                            {t('commissions.pending.paymentEligibility.ready')}
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell
                        w={ACTIONS_COLUMN_WIDTH}
                        {...stickyEndColumnProps(ACTIONS_COLUMN_WIDTH)}
                      >
                        <Flex w="full" justifyContent="flex-end">
                          <Button
                            size="xs"
                            variant="outline"
                            colorPalette="gray"
                            onClick={() => handlePaySingle(item)}
                            loading={
                              createPayoutMutation.isPending &&
                              createPayoutMutation.variables?.commissions?.[0]?.affiliateId ===
                                item.affiliateId
                            }
                            disabled={!isPaymentReady || createPayoutMutation.isPending}
                          >
                            {t('commissions.pending.pay')}
                          </Button>
                        </Flex>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table.Root>
        </TableScrollArea>
      )}
      <Flex justify="space-between" w="full" align="flex-start">
        <Text fontSize="15px" fontWeight="medium" color="gray.500">
          {t('commissions.pending.total', { count: commissions.length })}
        </Text>
      </Flex>
      <PendingCommissionsActionBar
        isOpen={hasSelection}
        selectedCount={selectedPayableCommissionKeys.length}
        onPaySelected={handlePaySelected}
        onClose={clearSelection}
        totalAmountSelected={selectedCommissionsTotal}
        currency={currencyCodeSelected}
        isLoading={createPayoutMutation.isPending}
      />
    </VStack>
  );
};
